import React, { useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../config/ConfigContext';
import useMediaQuery from '../hooks/useMediaQuery';
import { extractVideoId } from '../utils/youtubeUtils';

/**
 * Component that displays a horizontal row of lectures for a specific event
 * with YouTube thumbnail cards matching the homepage "Detailed Lecture Notes" style
 */
const EventSection = ({ eventName, lectures }) => {
  const { config, theme } = useConfig();
  const rowRef = useRef(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Sort lectures by lectureIdInSeries
  const sortedLectures = useMemo(() => {
    return [...lectures].sort((a, b) => {
      const aNum = parseInt(a.lectureIdInSeries) || 0;
      const bNum = parseInt(b.lectureIdInSeries) || 0;
      return aNum - bNum;
    });
  }, [lectures]);

  // Limit display
  const displayLectures = isMobile ? sortedLectures.slice(0, 4) : sortedLectures.slice(0, 8);

  const scrollRight = () => {
    if (rowRef.current) {
      rowRef.current.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };

  const scrollLeft = () => {
    if (rowRef.current) {
      rowRef.current.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };

  // Header text: "Lecture X" or date for recurring events
  const getHeaderText = (lecture, index) => {
    const hasNum = lecture.lectureIdInSeries && lecture.lectureIdInSeries.toString().trim() !== '';
    if (hasNum) return `Lecture ${lecture.lectureIdInSeries}`;
    if (lecture.date) {
      try {
        return new Date(lecture.date).toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric'
        });
      } catch { /* fall through */ }
    }
    return `Lecture ${index + 1}`;
  };

  return (
    <section style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
      padding: '24px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        paddingLeft: '8px',
        paddingRight: '8px'
      }}>
        <h2 style={{
          margin: 0,
          fontSize: '24px',
          fontWeight: 'bold',
          color: theme?.colors?.headingColor || theme?.colors?.primary || '#1e4273',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{
            display: 'inline-block',
            width: '4px',
            height: '24px',
            backgroundColor: theme?.colors?.primary || '#1e4273',
            borderRadius: '2px',
            marginRight: '8px'
          }}></span>
          {eventName}
        </h2>

        <Link to={`/events/${encodeURIComponent(eventName)}`} style={{
          color: theme?.colors?.linkColor || theme?.colors?.primary || '#1e4273',
          textDecoration: 'none',
          fontWeight: '600',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          borderRadius: '4px',
          whiteSpace: 'nowrap'
        }}>
          View all
          <span style={{ marginLeft: '6px', fontSize: '16px' }}>&#x2192;</span>
        </Link>
      </div>

      <div style={{
        position: 'relative',
        padding: isMobile ? '0 20px' : '0 40px',
        width: '100%',
        overflow: isMobile ? 'visible' : 'hidden'
      }}>
        {/* Scroll buttons */}
        {lectures.length > (isMobile ? 1 : 4) && (
          <>
            <button
              onClick={scrollLeft}
              aria-label="Scroll left"
              style={{
                position: 'absolute',
                left: isMobile ? '-10px' : '0',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                width: isMobile ? '30px' : '36px',
                height: isMobile ? '30px' : '36px',
                borderRadius: '50%',
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: isMobile ? '16px' : '20px'
              }}
            >
              &#x2190;
            </button>

            <button
              onClick={scrollRight}
              aria-label="Scroll right"
              style={{
                position: 'absolute',
                right: isMobile ? '-10px' : '0',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                width: isMobile ? '30px' : '36px',
                height: isMobile ? '30px' : '36px',
                borderRadius: '50%',
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: isMobile ? '16px' : '20px'
              }}
            >
              &#x2192;
            </button>
          </>
        )}

        {/* Horizontal scrolling container */}
        <div
          ref={rowRef}
          key={`scroll-container-${isMobile}`}
          style={{
            display: 'flex',
            flexDirection: 'row',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollBehavior: 'smooth',
            gap: '16px',
            padding: '8px 4px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          {displayLectures.length > 0 ? (
            displayLectures.map((lecture, index) => {
              const videoId = extractVideoId(lecture.fullVideoUrl || lecture.summaryVideoUrl);
              const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;

              return (
                <Link
                  key={lecture.lectureId}
                  to={`/lecture/${lecture.lectureId}`}
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    flex: isMobile ? '0 0 220px' : '0 0 260px',
                    minWidth: isMobile ? '220px' : '260px',
                    maxWidth: isMobile ? '220px' : '260px'
                  }}
                >
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    cursor: 'pointer',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.05)';
                  }}
                  >
                    {/* Header */}
                    <div style={{
                      backgroundColor: theme?.colors?.primary || '#1e4273',
                      padding: '10px 14px',
                      textAlign: 'center',
                      color: 'white',
                      borderRadius: '12px 12px 0 0'
                    }}>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        {getHeaderText(lecture, index)}
                      </div>
                    </div>

                    {/* YouTube Thumbnail */}
                    <div style={{
                      position: 'relative',
                      paddingTop: '56.25%',
                      backgroundColor: '#f3f4f6',
                      overflow: 'hidden'
                    }}>
                      {thumbnailUrl ? (
                        <img
                          src={thumbnailUrl}
                          alt={lecture.title}
                          style={{
                            position: 'absolute',
                            top: 0, left: 0,
                            width: '100%', height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div style={{
                          position: 'absolute',
                          top: '50%', left: '50%',
                          transform: 'translate(-50%, -50%)'
                        }}>
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                            <path d="M12 1C10.34 1 9 2.34 9 4V12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12V4C15 2.34 13.66 1 12 1Z" fill={theme?.colors?.primary || '#1e4273'} />
                            <path d="M17 11C17 11 17 12 17 12C17 14.76 14.76 17 12 17C9.24 17 7 14.76 7 12V11H5V12C5 15.64 7.81 18.64 11.28 18.97V22H12.72V18.97C16.19 18.64 19 15.64 19 12V11H17Z" fill={theme?.colors?.primary || '#1e4273'} />
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Info Box */}
                    <div style={{
                      backgroundColor: theme?.colors?.cardInfoBg || '#f0ece4',
                      background: `linear-gradient(to bottom, ${theme?.colors?.cardInfoBg || '#f0ece4'} 0%, ${theme?.colors?.cardInfoBgEnd || '#e8e3d9'} 100%)`,
                      padding: '12px',
                      textAlign: 'center',
                      minHeight: '70px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flex: 1
                    }}>
                      <h3 style={{
                        margin: '0 0 4px 0',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        color: theme?.colors?.primary || '#1e4273',
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {lecture.title || 'Untitled Lecture'}
                      </h3>
                      {lecture.date && (
                        <div style={{ fontSize: '11px', color: '#6c757d', marginTop: 'auto' }}>
                          {new Date(lecture.date).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric'
                          })}
                          {lecture.speaker && (
                            <>
                              <span style={{ margin: '0 4px' }}>•</span>
                              <span>{lecture.speaker}</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })
          ) : (
            <div style={{
              padding: '30px',
              textAlign: 'center',
              width: '100%',
              color: '#999'
            }}>
              No lectures available for this event yet.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default EventSection;
